import moment from "moment";
import {v4 as uuidv4} from "uuid";
import OriginalValidate from "@/mixins/originalValidate";
import axios from "axios";

import {initializeApp} from "firebase/app";
import {
    addDoc,
    collection,
    deleteDoc,
    deleteField,
    doc,
    getDoc,
    getDocs,
    getFirestore,
    increment,
    limit,
    onSnapshot,
    orderBy,
    query,
    setDoc,
    updateDoc,
    where,
    writeBatch,
    arrayUnion,
    startAfter
} from "firebase/firestore";
import {
    getAuth,
    updateEmail,
    verifyBeforeUpdateEmail,
    sendPasswordResetEmail
} from "firebase/auth";
import { getDownloadURL, ref, uploadBytes, getStorage } from "firebase/storage";
  


const app = initializeApp({
    apiKey: process.env.VUE_APP_apiKey,
    authDomain: process.env.VUE_APP_authDomain,
    projectId: process.env.VUE_APP_projectId,
    storageBucket: process.env.VUE_APP_storageBucket,
    messagingSenderId: process.env.VUE_APP_messagingSenderId,
    appId: process.env.VUE_APP_appId,
    measurementId: process.env.VUE_APP_measurementId
});


const auth = getAuth(app)
auth.languageCode = "ja"

export default {
    data: () => ({
        moment: moment,
        auth: auth,
        db: getFirestore(),
        storage: getStorage(app),
        doc: doc,
        addDoc: addDoc,
        setDoc: setDoc,
        getDoc: getDoc,
        getDocs: getDocs,
        deleteDoc: deleteDoc,
        writeBatch: writeBatch,
        collection_: collection,
        onSnapshot: onSnapshot,
        where: where,
        orderBy: orderBy,
        limit: limit,
        increment: increment,
        arrayUnion: arrayUnion,
        deleteField: deleteField,
        updateDoc: updateDoc,
        updateEmail: updateEmail,
        sendPasswordResetEmail: sendPasswordResetEmail,
        getDownloadURL: getDownloadURL,
        v: OriginalValidate,  // バリデーションをまとめる
        ref: ref,
        uploadBytes: uploadBytes,
        verifyBeforeUpdateEmail: verifyBeforeUpdateEmail,
        axios: axios,
        api: "https://scrape.datastructor.com",
    }),
    methods: {
        getCurrentUser() {
            return new Promise((resoleve, reject) => {
                const unsubscribe = this.auth.onAuthStateChanged(user => {
                    unsubscribe()
                    this.auth.currentUser = user
                    resoleve()
                }, reject)
            })
        },
        yyyy_mm_dd_hh_mm_ss(base = "YYYY-MM-DD HH:mm:ss") {
            return moment().format(base);
        },
        getMonthAgo(month = 1, unit = "month") {
            return moment().subtract(month, unit).format("YYYY-MM-DD HH:mm:ss")
        },
        // uuid設定
        uuid: () => uuidv4(),
        // authの問題があるため、このバリデーションのみoriginalValidate外に配置
        isLogin() {
            return !!auth.currentUser
        },
        async getToken() {
            return auth.currentUser.getIdToken(true)
        },
        // request
        makeConfig(url, method, params, headers, options) {
            return method === "GET" ?
                {method, url, headers, params, ...options} :
                {method, url, headers, data: params, ...options}
        },
        async request(url, method, params, headers = null) {
            let conf = this.makeConfig(url, method, params, headers)
            const res = await axios(conf)
            return res
        },
        /**apiアクセスのための共通関数
         *
         * @param url
         * @param method
         * @param params
         * @param headers
         * @param options
         * @returns {Promise<AxiosResponse<any>>}
         */
        async requestWithToken(url, method, params, headers = {}, options = {}) {
            await this.getCurrentUser()
            let token = ""
            await this.getToken().then(res => token = res)
            headers = {
                ...headers,
                Authorization: `Bearer ${token}`, "Content-Type": "application/json",
                uid: this.$store.getters["state/storeUser"].uid ? this.$store.getters["state/storeUser"].uid : this.auth.currentUser.uid
            };
            if (window.location.hostname === "localhost")
                url = confirm("ローカル環境にアクセスしますか？") ? url.replace(this.api, "http://0.0.0.0:8080"): url
            let conf = this.makeConfig(url, method, params, headers, options)
            return await axios(conf)
        },

        /** fsからデータ取得
         * @param ref リスト 例: ["config", "uid", "sian"]
         * @param isData bool データを取得するかどうか
         * @returns {Promise<*|*>}
         */
        async getDocument(ref, isData = true) {
            const docRef = this.doc(this.db, ...ref);
            const res = await this.getDoc(docRef)
            return isData ? res.data() : res;
        },
        async isExistsDocument(ref) {
            const docRef = this.doc(this.db, ...ref);
            const res = await this.getDoc(docRef)
            return res.exists()
        },
        /** fsからデータ取得
         * @param ref collectionで終わるリストを渡す 例: ["col1", "doc", "col2]
         * @param data
         * @param docIdNames
         * @returns {Promise<string>}
         *
         * @example
         * 1. collectionで終わるリストを渡す
         * 2. fieldデータ
         * 3. doc_idやユニークなidをリストに複数追加可能
         * this.addDocument(["app_scd"], scd, ["doc_id", "scd_id"])
         */
        async addDocument(ref, data, docIdNames = ["doc_id"]) {
            const colRef = this.collection_(this.db, ...ref);
            const docRef = await this.addDoc(colRef, data)
            const docId = docRef.id

            await this.sleep()

            const docIds = {}
            for (const n of docIdNames) {docIds[n] = docId;}
            await this.setDoc(docRef, docIds, {merge: true})
            return docId
        },
        /** fsのdocumentにデータを保存
         * @param ref リスト 例: ["config", "uid", "sian"]
         * @param data
         * @param isMerge bool
         */
        async setDocument(ref, data, isMerge = true) {
            const docRef = this.doc(this.db, ...ref);

            await this.setDoc(docRef, data, {merge: isMerge})
        },
        /** fsのdocumentにデータを保存
         * @param ref リスト 例: ["config", "uid", "sian"]
         * @param data
         */
        async updateDocument(ref, data) {
            const docRef = this.doc(this.db, ...ref);
            await this.updateDoc(docRef, data)
        },
        /** fsフィールドの特定の値削除
         * @param ref リスト 例: ["config", "uid", "sian"]
         * @param key: リスト 削除したいフィールドのkeyを渡す 例:["userHtml", "hoge"]
         */
        async deleteDocField(ref, key) {
            const docRef = this.doc(this.db, ...ref);
            if (key.length > 1) key = key.join(".")
            await this.updateDoc(docRef, {[key]: deleteField()})
        },
        /** fsドキュメントの削除
         * @param ref リスト 例: ["config", "uid", "sian"]
         */
        async deleteDocument(ref) {
            const docRef = this.doc(this.db, ...ref);
            await this.deleteDoc(docRef)
        },
        /** fsクエリからドキュメント取得
         * @param query 下記getQueryのreturn
         * @returns {Promise<*[]>}
         */
        async getDocsFromQuery(query) {
            const querySnapshot = await this.getDocs(query)
            const docList = []
            querySnapshot.forEach(doc => {
                docList.push(doc.data())
            })
            return docList
        },
        async getCollectionIds(ref) {
            const querySnapshot = await this.getDocs(this.collection_(this.db, ...ref))
            const docList = []
            querySnapshot.forEach(doc => {
                docList.push(doc.id)
            })
            return docList
        },
        async getCollections(ref) {
            const querySnapshot = await this.getDocs(this.collection_(this.db, ...ref))
            const docList = []
            querySnapshot.forEach(doc => {
                docList.push(doc.data())
            })
            return docList
        },

        /** fs用のクエリ生成
         * @param ref リスト 例: ["config", "uid", "sian"]
         * @param queries: records形式で渡す 例: [{field: "", term: "", value: ""}, {order: "", type: "desc"}, {limit: 100}]
         * where => {field: "", term: "", value: ""}
         * orderBy => {order: "", type: "desc"}
         * limit => {limit: 100}
         * 複数ある場合は、オブジェクトを追加
         * @returns {Query<DocumentData> | Query<unknown>}
         */
        getQuery(ref, queries) {
            // dataプロパティよりも先に呼び出されるため、大元のコレクション使用
            const col = this.collection_(this.db, ...ref)
            let q = queries.map(val => {
                if (val.field) return this.where(val.field, val.term, val.value)
                if (val.order) return this.orderBy(val.order, val.type)
                if (val.limit) return this.limit(val.limit)
                if (val.startAfter) return startAfter(...val.startAfter)
            })
            return query(col, ...q)
        },

        /** fs batch保存
         *
         * @param collection
         * @param records 配列で渡す 例: [{doc: str "", data: dict {}}], 注意: 500件以内
         */
        async batchData(collection, records) {
            const batch = writeBatch(this.db)

            records.forEach(obj => {
                let ref = doc(this.db, collection, obj.doc)
                batch.set(ref, obj.data, {merge: true})
            })

            await batch.commit();
        },

        mixinSearch(targets, word, keyNames = ["all"], partType = "part") {

            // 前提として完全一致の場合、検索対象は一つしかない
            // 検索対象が複数ある場合、完全一致できない {名前: 本吉, 属性: おじさん} === 名前 本吉 = 駄目です
            if (keyNames[0] === "all" && partType === "perfect") return ["Specify key for exact match"]

            const keys = keyNames[0] === "all" ? Object.keys(targets[0]) : keyNames
            return targets.filter((target) => {
                const words = keys.map((k) => target[k])
                // [abc, def]
                return partType === "part" ? new RegExp(word, "gi").test(words.join(' ')) : words[0] === word
            })
        },
        /** ローカルのクリップボードに保存(コピー)
         *
         * @param value
         * @returns {Promise<void>}
         */
        async doCopy(value) {
            await navigator.clipboard.writeText(value);
        },

        /** pythonのtime.sleepと同じ
         * @param ms:int ミリ秒で入力 例 2秒 = 2000
         * @returns {Promise<unknown>}
         */
        sleep(ms = 2000) {
            return new Promise((resolve) => {
                setTimeout(resolve, ms);
            });
        },

        mixinDownloadCsv(records, name) {
            let csv = Object.keys(records[0]) + '\n'
            const noProcess = ["Description"]
            // todo: 20220703 バグったら戻す
            //     records.forEach(val => {
            //         let itemData = Object.values(val).map(v => JSON.stringify(v))
            //         csv += itemData + "\n"
            //     })
            records.forEach(obj => {
                let itemData = []
                for (let key in obj) {
                    itemData.push(
                        noProcess.includes(key) ?
                            obj[key] : JSON.stringify(obj[key])
                    )
                }
                csv += itemData + "\n"
            })
            let blob = new Blob([csv], {type: "text/csv"})
            let link = document.createElement("a")
            link.href = window.URL.createObjectURL(blob)
            link.download = `${name}.csv`
            link.click()
            return "success"
        },
        makeCsvData(records) {
            let csv = Object.keys(records[0]) + '\n'
            const noProcess = ["Description"]
            records.forEach(obj => {
                let itemData = []
                for (let key in obj) {
                    itemData.push(
                        noProcess.includes(key) ?
                            obj[key] : JSON.stringify(obj[key])
                    )
                }
                csv += itemData + "\n"
            })
            return new Blob([csv], {type: "text/csv"})
        },
        round2Two(num, rou = 2) {
            return +(Math.round(num + `e+${rou}`) + `e-${rou}`)
        },
        /** v-forの:keyに渡すユニークなkeyを生成
         * @param item: スカラー値を渡す, NG: [], {}, (), OK: "abc", 123
         * @param idx:
         * @returns {string}
         */
        uniKey(item, idx) {
            return `${item}-${idx}`
        },
        /**
         * 文字列の一部をアスタリスクにして、隠す
         * @param text{String}: 隠したい文字列
         * @param start{Number}: 最初の位置
         * @param end{Number}: 最後の位置
         * @returns {string}
         */
        hideText(text = "", start = 4, end = -4) {
            const secret = text.slice(start, end);
            return text.replace(secret, "***");
        },
        /**
         * ローカルと本番でパス変更
         * @returns {string}
         */
        changeEnviron() {
            return document.domain === "localhost" ?
                "http:0.0.0.0:8080/" : "https://api-exponential.awiiin.com/"
        },
        /**スナックバー更新
         * @param color str 色
         * @param msg str メッセージ
         * @param timeout int 表示時間
         */
        changeStateSnackbar(color, msg, timeout = 1500) {
            const snackbar = {
                flag: true,
                color: color,
                msg: msg,
                timeout: timeout
            }
            return this.$store.dispatch("state/changeSnackbar", snackbar)
        },
        /**
         * ゼロ埋め
         * @param num ゼロ埋めしたい数値
         * @param len 桁数
         * @returns {string}
         */
        zeroPadding(num, len) {
            return (Array(len).join('0') + num).slice(-len);
        },
        toHorizontal(data, target) {
            let base = Object.fromEntries(Object.entries(data).filter(([k]) => k !== target));
            if (!Array.isArray(data[target])) {
                return {...base, ...Object.fromEntries(Object.entries(data[target]).map(([k, v]) => ([`${target}_${k}`, v])))}
            }
            for (let i = 0; i < data[target].length; i++) {
                const obj = data[target][i];
                if (typeof obj === 'object' && !Array.isArray(obj)) {
                    base = {...base, ...Object.fromEntries(Object.entries(obj).map(([k, v]) => ([`${target}_${k}_${i}`, v])))}
                } else {
                    base[`${target}_${i}`] = obj;
                }
            }
            return base;
        },
        toVertical(data, target) {
            const results = [];
            const base = Object.fromEntries(Object.entries(data).filter(([key]) => key !== target));
            for (const o of data[target]) {
                if (typeof o === 'object' && o !== null) {
                    results.push({...base, ...Object.fromEntries(Object.entries(o).map(([k, v]) => [`vertical_${target}_${k}`, v]))});
                } else {
                    results.push({...base, [`vertical_${target}`]: o});
                }
            }
            if (results.length === 0) results.push({...base, [`vertical_${target}`]: null});
            return results;
        },
        domainToPascal(url) {
            const domain = new URL(url).hostname;
            return domain.split(/[^A-Za-z]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('');
        },

        /**
         * 今月の初ログインか判定
         * @param counterData Firestoreのcounterドキュメント
         * @returns {boolean}
         */
        isFirstLoginThisMonth(counterData) {
            // データがない場合 → 初ログイン扱い
            if (!counterData || !counterData.last_login) return true;
    
            const last = new Date(counterData.last_login);
            const now = new Date();
    
            // 年と月が同じなら今月中のログイン → false
            return !(last.getFullYear() === now.getFullYear() && last.getMonth() === now.getMonth());
        },
    
        /**
         * 月初ログイン時にカウンターをリセット
         * @param uid ログインユーザーID
         * @param counterData 既存カウンターデータ
         */
        async resetMonthlyCounters(uid, counterData) {
            const ref = this.doc(this.db, "counter", uid);
    
            const newData = {
            ...counterData,
            last_login: new Date().toISOString(),
            monthly_count: 0,
            };
    
            await this.setDoc(ref, newData, { merge: true });
            console.log("月初リセット完了:");
        },
        deepCopy: (obj) => JSON.parse(JSON.stringify(obj)),
    },
}